import * as React from 'react';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';

import Input from 'components/inputs/input/Input';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import BrandsFilter from 'components/brands-filter/BrandsFilter';

import useManagedInputValue from 'hooks/use-managed-input-value';

import { __t, applyTranslations } from 'utils/language';
import { availableBrandFilterOptionsSelector } from 'selectors/brand-filters/brandFilters';

import './campaigns-filters-mixed.scss';

applyTranslations(require('./campaigns-filters-mixed.i18n.json'));

import type {
  CampaignType,
  SelectOption
} from 'types';

type OwnProps = {
  withCcm: boolean,
  withBrand: boolean,
  search: string,
  campaignTypeCheckboxes: Array<CampaignType>,
  onSearchChange: (search: string) => void,
  onCheckboxChange: (campaignType: CampaignType) => void
};

type StateProps = {
  brandFilterOptions: Array<SelectOption>
};

type Props = OwnProps & StateProps;

const MSG_KEY = 'campaigns_filters_mixed';

const CampaignsFiltersMixed: React.FC<Props> = ({
  withCcm,
  withBrand,
  brandFilterOptions,
  search,
  campaignTypeCheckboxes = [],
  onSearchChange,
  onCheckboxChange
}): JSX.Element => {
  const debounceOnSearchChange = React.useCallback(debounce(onSearchChange, 500), [onSearchChange]);
  const [searchQuery, setSearchQuery] = useManagedInputValue(search);

  const renderCheckbox = (
    campaignType: CampaignType
  ): JSX.Element => (
    <InputCheckbox
      key={`campaigns-filters-mixed__checkbox-${campaignType}`}
      id={`campaigns-filters-mixed__checkbox-${campaignType}`}
      name="campaignType"
      label={__t(`${MSG_KEY}.checkboxes.${campaignType}`)}
      value={String(campaignTypeCheckboxes.includes(campaignType))}
      checked={campaignTypeCheckboxes.includes(campaignType)}
      onChange={() => onCheckboxChange(campaignType)}
    />
  );

  const renderCheckboxes = (): React.ReactNode => (
    <div className="campaigns-filters-mixed__checkboxes">
      {
        ['collab', 'ccm'].map(campaignType => (
          renderCheckbox(campaignType as CampaignType)
        ))
      }
    </div>
  );

  const renderSearchField = (): React.ReactNode => (
    <div className="campaigns-filters-mixed__search">
      <Input
        name="search"
        width="responsive"
        value={searchQuery}
        autoComplete="off"
        placeholder={`${__t(`${MSG_KEY}.placeholders.search`)}`}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const target = event.currentTarget;
          const value = target.value || '';

          setSearchQuery(value);
          debounceOnSearchChange(value);
        }}
      />
    </div>
  );

  return (
    <div className="campaigns-filters-mixed">
      <div className="campaigns-filters-mixed__search">
        {
          brandFilterOptions.length > 1 && (
            <div className="campaigns-filters-mixed__dropdown">
              <BrandsFilter hideLabel />
            </div>
          )
        }
        {
          renderSearchField()
        }
      </div>
      {
        withBrand && withCcm &&
          renderCheckboxes()
      }
    </div>
  );
};

const mapStateToProps = (state): StateProps => ({
  brandFilterOptions: availableBrandFilterOptionsSelector(state)
});

export default connect(mapStateToProps)(CampaignsFiltersMixed);
